import React from 'react';

/**
 * Read-only display of the current subscription
 *
 * @param {Object} subscription - Current subscription object
 * @param {Array} items - Subscription items
 * @param {Array} products - All products (for grouping)
 * @param {Function} onUpdate - Called when "Update Plan" clicked
 * @param {Function} onCancel - Called when "Cancel Subscription" clicked
 * @param {boolean} showActions - Whether to show Update/Cancel buttons
 * @param {boolean} muted - Whether to show in muted/gray style (for side-by-side)
 * @param {boolean} isExempt - Whether billing is exempt (hides pricing info)
 */
export function CurrentSubscriptionCard({
  subscription,
  items,
  products: _products,
  onUpdate,
  onCancel,
  showActions = true,
  muted = false,
  isExempt = false
}) {
  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  // Group items by product
  const groupedItems = {};
  items.forEach(item => {
    const productId = item.product?.id || item.level?.product_id || 'unknown';
    const productName = item.product?.name || 'Unknown Product';
    if (!groupedItems[productId]) {
      groupedItems[productId] = {
        name: productName,
        items: []
      };
    }
    groupedItems[productId].items.push(item);
  });

  // Calculate total
  const total = items.reduce((sum, item) => {
    return sum + (item.current_price_cents || 0);
  }, 0);

  const billingInterval = subscription?.billing_interval || 'month';
  const intervalLabel = billingInterval === 'year' ? 'year' : 'month';

  const cardStyle = {
    border: '1px solid var(--bbs-border-primary, #e5e7eb)',
    borderRadius: '8px',
    padding: '20px',
    backgroundColor: muted ? 'var(--bbs-bg-muted, #f9fafb)' : 'var(--bbs-bg-primary, white)',
    opacity: muted ? 0.8 : 1
  };

  const textColor = muted ? 'var(--bbs-text-secondary, #6b7280)' : 'var(--bbs-text-primary, #1f2937)';

  return (
    <div style={cardStyle}>
      {/* Header */}
      <h3 style={{
        margin: '0 0 8px 0',
        fontSize: '16px',
        fontWeight: '600',
        color: textColor
      }}>
        Current Subscription
      </h3>

      {!isExempt && (
        <p style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          color: muted ? 'var(--bbs-text-muted, #9ca3af)' : 'var(--bbs-text-secondary, #6b7280)'
        }}>
          Billed {billingInterval === 'year' ? 'Yearly' : 'Monthly'}
        </p>
      )}

      {/* Grouped Items */}
      {Object.entries(groupedItems).map(([productId, group]) => (
        <div key={productId} style={{ marginBottom: '16px' }}>
          <h4 style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            fontWeight: '600',
            color: muted ? 'var(--bbs-text-muted, #9ca3af)' : 'var(--bbs-text-primary, #374151)'
          }}>
            {group.name}
          </h4>
          {group.items.map(item => {
            const levelName = item.level?.name || item.product_level?.name || 'Plan';
            const pricePerUnit = item.unit_price_cents || 0;
            const licenseCount = item.license_count || 1;

            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 0',
                  fontSize: '14px',
                  color: textColor
                }}
              >
                <span style={{ width: '80px' }}>{levelName}</span>
                <span style={{ color: muted ? 'var(--bbs-text-muted, #9ca3af)' : 'var(--bbs-text-secondary, #6b7280)' }}>
                  {isExempt
                    ? `${licenseCount} license${licenseCount !== 1 ? 's' : ''}`
                    : `${licenseCount} @ ${formatPrice(pricePerUnit)} per ${intervalLabel}`
                  }
                </span>
                {!isExempt && (
                  <>
                    <span style={{
                      flex: 1,
                      borderBottom: `1px dotted var(--bbs-border-secondary, ${muted ? '#e5e7eb' : '#d1d5db'})`,
                      margin: '0 8px',
                      height: '1px',
                      alignSelf: 'flex-end',
                      marginBottom: '6px'
                    }} />
                    <span style={{ fontWeight: '500' }}>
                      {formatPrice(item.current_price_cents || pricePerUnit * licenseCount)}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Total */}
      {!isExempt && (
        <div style={{
          textAlign: 'right',
          fontSize: '14px',
          color: textColor,
          marginBottom: '12px'
        }}>
          <strong>Total {formatPrice(total)} per {intervalLabel}</strong>
        </div>
      )}

      {/* Current Term / Trial Status */}
      {!isExempt && (
        <p style={{
          margin: '0 0 16px 0',
          fontSize: '13px',
          color: muted ? 'var(--bbs-text-muted, #9ca3af)' : 'var(--bbs-text-secondary, #6b7280)'
        }}>
          {subscription?.status === 'trialing' ? (
            <>
              <span style={{ color: 'var(--bbs-accent-trial, #059669)', fontWeight: '500' }}>Trial</span>
              {' '}ends {formatDate(subscription?.trial_ends_at)}
            </>
          ) : (
            <>Current term {formatDate(subscription?.current_period_start)} to {formatDate(subscription?.current_period_end)}</>
          )}
        </p>
      )}

      {/* Actions */}
      {showActions && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '12px',
          borderTop: '1px solid var(--bbs-border-primary, #e5e7eb)'
        }}>
          <button
            onClick={onUpdate}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--bbs-bg-primary, white)',
              color: 'var(--bbs-text-primary, #374151)',
              border: '1px solid var(--bbs-border-secondary, #d1d5db)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Update Plan
          </button>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: 'var(--bbs-accent-danger, #dc2626)',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel Subscription
          </button>
        </div>
      )}
    </div>
  );
}

export default CurrentSubscriptionCard;
